/**
 * TopNav — fixed, blur-backed pill nav that hides on scroll down and returns
 * on scroll up. It also owns the prototype's shared hamburger menu, account
 * actions, and contact pop-up.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { signOut } from '../lib/useAuth';
import './TopNav.css';

interface TopNavProps {
  title: string;
  onBack?: () => void;
  /** Crumb/badge shown on the right (e.g. progress %). */
  crumb?: string;
  /** Optional contextual action shown before the menu button. */
  action?: ReactNode;
  /** Optional callback fired when the title is tapped. Used for hidden dev
   *  tooling (e.g. 10 quick taps toggles the DevPanel on the Book page). */
  onTitleTap?: () => void;
}

type MenuIconName = 'home' | 'learning' | 'idea' | 'alerts';

const menuLinks: { label: string; path: string; icon: MenuIconName }[] = [
  { label: 'Home', path: '/', icon: 'home' },
  { label: 'Learning Hub', path: '/learning', icon: 'learning' },
  { label: 'Idea Dashboard', path: '/idea', icon: 'idea' },
  { label: 'Alert Centre', path: '/alerts', icon: 'alerts' },
];

function DrawerIcon({ name }: { name: MenuIconName }) {
  if (name === 'home') {
    return <><path d="M3 9.5 12 3l9 6.5" /><path d="M5 10v10h14V10" /></>;
  }

  if (name === 'learning') {
    return <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>;
  }

  if (name === 'idea') {
    return <><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.4 1 2.3h6c0-.9.4-1.8 1-2.3A7 7 0 0 0 12 2z" /></>;
  }

  return <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>;
}

export function TopNav({ title, onBack, crumb, action, onTitleTap }: TopNavProps) {
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const lastY = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // Hide on scroll down past 80px; show on scroll up.
      if (y > lastY.current && y > 80) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setContactOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen && !contactOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (contactOpen) {
        setContactOpen(false);
      } else {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [contactOpen, menuOpen]);

  const goTo = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    await signOut();
    session.signOut();
    setMenuOpen(false);
    navigate('/signin');
  };

  return (
    <>
      <nav className={`itk-top-nav ${hidden ? 'itk-top-nav--hidden' : ''}`.trim()} aria-label={`${title} navigation`}>
        {onBack && (
          <button type="button" className="itk-nav-back" onClick={onBack} aria-label="Go back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
        )}
        <span
          className="itk-nav-title"
          onClick={onTitleTap}
          style={onTitleTap ? { cursor: 'default', userSelect: 'none' } : undefined}
        >
          {title}
        </span>
        {crumb && <span className="itk-nav-crumb">{crumb}</span>}
        {action && <div className="itk-nav-action">{action}</div>}
        <button
          type="button"
          className="itk-nav-burger"
          aria-label="Open menu"
          aria-controls="itk-menu-drawer"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </nav>

      <div
        className={`itk-menu-scrim ${menuOpen ? 'itk-menu-scrim--open' : ''}`.trim()}
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />
      <aside
        id="itk-menu-drawer"
        className={`itk-menu-drawer ${menuOpen ? 'itk-menu-drawer--open' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label="Idea Toolkit menu"
        aria-hidden={!menuOpen}
      >
        <div className="itk-drawer-head">
          <div>
            <div className="itk-drawer-brand-name">Idea Toolkit</div>
            <div className="itk-drawer-brand-by">by Innovate Design</div>
          </div>
          <button type="button" className="itk-drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="itk-drawer-nav" aria-label="Primary navigation">
          {menuLinks.map((link) => (
            <button
              key={link.path}
              type="button"
              className={`itk-drawer-link ${location.pathname === link.path ? 'itk-drawer-link--active' : ''}`.trim()}
              onClick={() => goTo(link.path)}
            >
              <span className="itk-drawer-link-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <DrawerIcon name={link.icon} />
                </svg>
              </span>
              {link.label}
            </button>
          ))}
        </nav>

        <button type="button" className="itk-drawer-cta" onClick={() => goTo('/book')}>
          <span>
            <span className="itk-drawer-cta-title">Book idea review</span>
            <span className="itk-drawer-cta-subtitle">Book your free call</span>
          </span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className="itk-drawer-spacer" />

        <div className="itk-drawer-account">
          <div className="itk-drawer-account-label">Customer email address</div>
          <div className={`itk-drawer-account-email ${session.email ? '' : 'itk-drawer-account-email--none'}`.trim()}>
            {session.email ?? 'Not signed in'}
          </div>
          <div className="itk-drawer-foot">
            <button type="button" className="itk-drawer-foot-button" onClick={() => setContactOpen(true)}>Contact</button>
            <span className="itk-drawer-foot-separator" aria-hidden="true">|</span>
            <button type="button" className="itk-drawer-foot-button" onClick={() => goTo('/settings')}>Settings</button>
            <span className="itk-drawer-foot-separator" aria-hidden="true">|</span>
            <button type="button" className="itk-drawer-foot-button itk-drawer-foot-button--signout" onClick={handleSignOut}>Sign out</button>
          </div>
        </div>
      </aside>

      <div
        className={`itk-contact-scrim ${contactOpen ? 'itk-contact-scrim--open' : ''}`.trim()}
        aria-hidden="true"
        onClick={() => setContactOpen(false)}
      />
      <section
        className={`itk-contact-popover ${contactOpen ? 'itk-contact-popover--open' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="itk-contact-title"
        aria-hidden={!contactOpen}
      >
        <div className="itk-contact-head">
          <div id="itk-contact-title" className="itk-contact-title">Contact us</div>
          <button type="button" className="itk-contact-close" onClick={() => setContactOpen(false)} aria-label="Close contact details">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <a className="itk-contact-row" href="tel:+442073545640">
          <span className="itk-contact-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </span>
          <span className="itk-contact-body"><span className="itk-contact-label">Call</span><span className="itk-contact-value">020 7354 5640</span></span>
        </a>
        <a className="itk-contact-row" href="mailto:info@innovate-design.co.uk">
          <span className="itk-contact-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="3 6 12 13 21 6" />
            </svg>
          </span>
          <span className="itk-contact-body"><span className="itk-contact-label">Email</span><span className="itk-contact-value">info@innovate-design.co.uk</span></span>
        </a>
      </section>
    </>
  );
}